// Per-destination caption override — the prefill on tick, and the warning that
// has to come with it (Scott, 2026-09-30). Pure, so the sentences are asserted
// without jsdom (the reportCopy.ts posture).
//
// ⚠️⚠️ THE TRAP THE PREFILL CREATES, AND WHY THE WARNING EXISTS. An override is
// a FROZEN COPY: once a destination has its own caption, editing the main
// caption afterwards does not reach it. While the box started empty that was
// visible — you had typed the override yourself. Prefilled with the main
// caption, the two look identical until the main one changes, and then that
// destination silently keeps the old wording. So the warning says what it
// MEANS ("will not pick up changes"), not merely that an override exists.
// Weakening that wording defeats the item.
//
// ⚠️ Stories are media-only (no caption field) and never carry an override;
// they are excluded here as well as in the card.

import { destinationById, DESTINATION_ORDER, type PostDestinationId } from './destinations';

type OverrideFields = { overrideCaption: boolean; customContent: string };
type TargetLike = OverrideFields & { enabled: boolean };

/**
 * The patch applied when the "Caption override" box is ticked or unticked.
 * Ticking an EMPTY override prefills it with the main caption; ticking one that
 * already holds text (unticking keeps the typed value) leaves that text alone.
 * Unticking never clears it.
 */
export function captionOverrideTickPatch(
  target: OverrideFields,
  checked: boolean,
  baseCaption: string,
): Partial<OverrideFields> {
  if (checked && target.customContent.trim() === '') {
    return { overrideCaption: true, customContent: baseCaption };
  }
  return { overrideCaption: checked };
}

/** An override that is actually in effect — the same rule effectiveCaption applies. */
export function hasOwnCaption(id: PostDestinationId, t: TargetLike): boolean {
  return (
    t.enabled &&
    destinationById(id).placement !== 'story' &&
    t.overrideCaption &&
    t.customContent.trim() !== ''
  );
}

/** The sentence on a destination's own options block. */
export function frozenCaptionWarning(destinationLabel: string): string {
  return `${destinationLabel} has its own caption. It will not pick up changes to the main caption.`;
}

/** Labels of every destination whose caption is its own, in display order. */
export function destinationsWithOwnCaption(
  targets: Record<PostDestinationId, TargetLike>,
): string[] {
  return DESTINATION_ORDER.filter((id) => hasOwnCaption(id, targets[id])).map(
    (id) => destinationById(id).label,
  );
}

/** The notice at the top of the composer, beside the main caption; null when none apply. */
export function frozenCaptionSummary(labels: readonly string[]): string | null {
  if (labels.length === 0) return null;
  if (labels.length === 1) return frozenCaptionWarning(labels[0]!);
  const list =
    labels.length === 2
      ? `${labels[0]} and ${labels[1]}`
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  return `${list} have their own captions. They will not pick up changes to the main caption.`;
}
